import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import moment from "moment";

import { CustomCalendar } from "components/CustomCalendar/CustomCalendar";
import { usePaymentsByService } from "features/payments/hooks/usePaymentsByService";
import { useServiceCountsPerDay } from "features/payments/hooks/useServiceCountsPerDay";

import styles from "../PaymentsPage.module.css";

const PaymentsDashboard = ({
  dashboardStartDate,
  dashboardEndDate,
  setDashboardStartDate,
  setDashboardEndDate,
  paymentMetrics,
}) => {
  const { data: paymentsByServiceData } = usePaymentsByService({
    startDate: dashboardStartDate,
    endDate: dashboardEndDate,
    enabled: true,
  });

  const { data: serviceCountsData } = useServiceCountsPerDay({
    startDate: dashboardStartDate,
    endDate: dashboardEndDate,
    enabled: true,
  });

  const paymentsByServiceChart = useMemo(() => {
    const data = paymentsByServiceData || [];
    const categories = data.map((item) => item.serviceName || "Unknown");
    const amounts = data.map((item) => parseFloat(item.totalAmount || 0));

    return {
      chart: { type: "bar", height: 300 },
      title: { text: null },
      xAxis: {
        categories: categories.length ? categories : ["No Data"],
        title: { text: null },
      },
      yAxis: {
        title: { text: "Amount (€)" },
        min: 0,
      },
      series: [
        {
          name: "Amount Paid",
          data: amounts.length ? amounts : [0],
          color: "#059669",
        },
      ],
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          return "<b>" + this.x + "</b><br/>Amount: €" + this.y.toFixed(2);
        },
      },
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
            format: "€{y:.2f}",
          },
        },
      },
    };
  }, [paymentsByServiceData]);

  const serviceCountsChart = useMemo(() => {
    const data = serviceCountsData || [];

    if (!data.length) {
      return {
        chart: { type: "line", height: 300 },
        title: { text: null },
        xAxis: { categories: ["No Data"] },
        yAxis: { title: { text: "Count" } },
        series: [{ name: "No Data", data: [0] }],
      };
    }

    const categories = data.map((item) => moment(item.date).format("DD/MM"));

    const serviceNames = new Set();
    data.forEach((day) => {
      (day.services || []).forEach((service) => {
        serviceNames.add(service.serviceName || "Unknown");
      });
    });

    const colors = [
      "#3b82f6",
      "#059669",
      "#f59e0b",
      "#ef4444",
      "#8b5cf6",
      "#ec4899",
      "#06b6d4",
      "#84cc16",
    ];

    const series = Array.from(serviceNames).map((serviceName, index) => ({
      name: serviceName,
      data: data.map((day) => {
        const service = (day.services || []).find(
          (s) => (s.serviceName || "Unknown") === serviceName
        );

        return service ? service.count : 0;
      }),
      color: colors[index % colors.length],
    }));

    return {
      chart: { type: "line", height: 300 },
      title: { text: null },
      xAxis: {
        categories,
        title: { text: "Date" },
      },
      yAxis: {
        title: { text: "Count" },
        min: 0,
        allowDecimals: false,
      },
      series: series.length ? series : [{ name: "No Data", data: [0] }],
      legend: { enabled: true },
      tooltip: {
        shared: true,
        crosshairs: true,
      },
    };
  }, [serviceCountsData]);
  return (
    <>
      <div className={styles.filtersGrid}>
        <div className={styles.filterField}>
          <label className={styles.filterLabel}>Start Date</label>
          <CustomCalendar
            value={dashboardStartDate.toDate()}
            onChange={(date) => setDashboardStartDate(moment(date))}
            placeholder="Select start date"
            maxDate={dashboardEndDate.toDate()}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel}>End Date</label>
          <CustomCalendar
            value={dashboardEndDate.toDate()}
            onChange={(date) => setDashboardEndDate(moment(date))}
            placeholder="Select end date"
            minDate={dashboardStartDate.toDate()}
            maxDate={new Date()}
          />
        </div>
      </div>

      <div className={styles.metricsGrid}>
        <div className={styles.metricCard}>
          <h3 className={styles.metricTitle}>Daily Average Payments Due</h3>
          <p className={styles.metricValue}>
            €
            {paymentMetrics.dailyAveragePaymentsDue?.data?.averageDailyPaymentsDue?.toFixed(
              2
            ) || "0.00"}
          </p>
        </div>
        <div className={styles.metricCard}>
          <h3 className={styles.metricTitle}>Daily Average Payments Made</h3>
          <p className={`${styles.metricValue} ${styles.metricGreen}`}>
            €
            {paymentMetrics.dailyAveragePaymentsMade?.data?.averageDailyPaymentsMade?.toFixed(
              2
            ) || "0.00"}
          </p>
        </div>
        <div className={styles.metricCard}>
          <h3 className={styles.metricTitle}>Average Days Open</h3>
          <p className={`${styles.metricValue} ${styles.metricRed}`}>
            {paymentMetrics.averageDaysOpen?.data?.averageDaysOpen?.toFixed(
              1
            ) || "0.0"}{" "}
            days
          </p>
        </div>
      </div>

      <div className={styles.chartCard}>
        <h3 className={styles.chartTitle}>Payment Due / Paid</h3>
        <HighchartsReact
          highcharts={Highcharts}
          options={{
            chart: { type: "line", height: 400 },
            title: { text: null },
            xAxis: {
              categories:
                paymentMetrics.combinedDailyPaymentSummary?.data?.map((item) =>
                  moment(item.date).format("DD/MM")
                ) || ["No Data"],
              title: { text: "Date" },
            },
            yAxis: { title: { text: "Amount (€)" } },
            series: [
              {
                name: "Total Amount Due",
                data: paymentMetrics.combinedDailyPaymentSummary?.data?.map(
                  (item) => parseFloat(item.totalAmountDue || 0)
                ) || [0],
                color: "#dc2626",
              },
              {
                name: "Total Amount Paid",
                data: paymentMetrics.combinedDailyPaymentSummary?.data?.map(
                  (item) => parseFloat(item.totalAmountPaid || 0)
                ) || [0],
                color: "#059669",
              },
            ],
            legend: { enabled: true },
            tooltip: {
              formatter: function () {
                return (
                  "<b>" +
                  this.series.name +
                  "</b><br/>" +
                  this.x +
                  ": €" +
                  this.y.toFixed(2)
                );
              },
            },
          }}
        />
      </div>

      <div className={styles.chartsRow}>
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>Amount Paid by Service</h3>
          <HighchartsReact
            highcharts={Highcharts}
            options={paymentsByServiceChart}
          />
        </div>

        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>Service Counts per Day</h3>
          <HighchartsReact
            highcharts={Highcharts}
            options={serviceCountsChart}
          />
        </div>
      </div>
    </>
  );
};

export { PaymentsDashboard };
